/**
 * DEC-328 — sync nudges and driver check-ins (migration 0153; quick 261013-snd).
 * Spec: docs/CLAUDE.md §44 (notify driver first), §45 / §47 (analytics).
 *
 * - `driver_checkins`: the SERVER's time of a driver's last check-in (an accepted sync
 *   upload or a reference-data refresh). job_events.occurred_at is the phone's clock
 *   and cannot answer "did the driver sync after we asked?".
 * - `sync_nudges`: one row per dispatcher "ask driver to sync". Resolved (synced within
 *   2 hours, Corbin D-C) is derived from first_checkin_after_at - sent_at, never stored.
 *   Push delivery outcomes live in notification_deliveries (occurrence id = this id).
 *
 * Both tables are tenant-scoped under RLS (USING + WITH CHECK on app.current_tenant_id).
 */
import {
  check,
  index,
  integer,
  pgPolicy,
  pgTable,
  primaryKey,
  timestamp,
  uuid,
} from 'drizzle-orm/pg-core';
import { sql } from 'drizzle-orm';
import { appUserRole } from './enums.js';
import { tenants, users } from './users.js';
import { jobs } from './jobs.js';

const tenantIsolation = sql`tenant_id::text = current_setting('app.current_tenant_id', true)`;

export const driverCheckins = pgTable(
  'driver_checkins',
  {
    tenantId: uuid('tenant_id')
      .notNull()
      .references(() => tenants.id, { onDelete: 'cascade' }),
    userId: uuid('user_id')
      .notNull()
      .references(() => users.id, { onDelete: 'cascade' }),
    lastCheckinAt: timestamp('last_checkin_at', { withTimezone: true }).notNull(),
  },
  (t) => [
    primaryKey({ name: 'driver_checkins_pkey', columns: [t.tenantId, t.userId] }),
    pgPolicy('driver_checkins_tenant_isolation', {
      as: 'permissive',
      to: appUserRole,
      for: 'all',
      using: tenantIsolation,
      withCheck: tenantIsolation,
    }),
  ],
);

export type DriverCheckinRow = typeof driverCheckins.$inferSelect;

export const syncNudges = pgTable(
  'sync_nudges',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    tenantId: uuid('tenant_id')
      .notNull()
      .references(() => tenants.id, { onDelete: 'cascade' }),
    jobId: uuid('job_id')
      .notNull()
      .references(() => jobs.id, { onDelete: 'cascade' }),
    /** The job's assigned driver when the nudge was sent (users.id). */
    driverId: uuid('driver_id')
      .notNull()
      .references(() => users.id, { onDelete: 'cascade' }),
    sentById: uuid('sent_by_id')
      .notNull()
      .references(() => users.id, { onDelete: 'cascade' }),
    sentAt: timestamp('sent_at', { withTimezone: true }).notNull().defaultNow(),
    pushQueuedCount: integer('push_queued_count').notNull(),
    pushSuppressedCount: integer('push_suppressed_count').notNull(),
    /** Last check-in before the nudge; NULL if the driver never checked in. */
    lastCheckinBeforeAt: timestamp('last_checkin_before_at', { withTimezone: true }),
    /** First check-in after the nudge, stamped by the check-in writer. */
    firstCheckinAfterAt: timestamp('first_checkin_after_at', { withTimezone: true }),
  },
  (t) => [
    index('sync_nudges_open_by_driver_idx')
      .on(t.tenantId, t.driverId)
      .where(sql`${t.firstCheckinAfterAt} IS NULL`),
    index('sync_nudges_job_sent_idx').on(t.jobId, t.sentAt.desc()),
    index('sync_nudges_tenant_sent_idx').on(t.tenantId, t.sentAt),
    check(
      'sync_nudges_push_counts_nonneg',
      sql`${t.pushQueuedCount} >= 0 AND ${t.pushSuppressedCount} >= 0`,
    ),
    check(
      'sync_nudges_checkin_after_sent',
      sql`${t.firstCheckinAfterAt} IS NULL OR ${t.firstCheckinAfterAt} >= ${t.sentAt}`,
    ),
    check(
      'sync_nudges_checkin_before_sent',
      sql`${t.lastCheckinBeforeAt} IS NULL OR ${t.lastCheckinBeforeAt} <= ${t.sentAt}`,
    ),
    pgPolicy('sync_nudges_tenant_isolation', {
      as: 'permissive',
      to: appUserRole,
      for: 'all',
      using: tenantIsolation,
      withCheck: tenantIsolation,
    }),
  ],
);

export type SyncNudgeRow = typeof syncNudges.$inferSelect;
export type NewSyncNudgeRow = typeof syncNudges.$inferInsert;
